import { useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../components/ui';
import { RecoveryPointsView } from '../../components/RecoveryPointsView';
import type { RecoveryIntent } from '../../components/RecoveryPointsView';
import { ExecutionRunView } from '../../components/ExecutionRunView';
import { ProjectSection } from '../../components/project/ui';
import { ImportStorePanel } from '../../components/project/ImportStorePanel';
import { useProject } from './ProjectContext';
import { SSHEnvironmentNotice, isOperatedOverSSH } from '../../components/project/SSHEnvironmentNotice';
import { BackupPosition } from '../../components/project/BackupPosition';

/**
 * RecoverySection holds the environment's recovery points and everything
 * done with them — take a backup, rehearse a restore, restore, pin, delete,
 * schedule and retention. Every destructive step keeps the confirmation it
 * already had; nothing here was loosened by the move.
 *
 * It is also where a store that runs elsewhere is brought in (a Magento
 * project only): above the backups while nothing is deployed yet — the
 * import comes before the first deployment — and below them afterwards,
 * for the final copy before DNS moves.
 */
export function RecoverySection() {
  const project = useProject();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { environment, recoveryPoints } = project;
  const openRunId = searchParams.get('run');
  // One-shot intents from an incident's suggested actions (UX-01).
  const intent = searchParams.get('intent');
  const point = searchParams.get('point');
  const recoveryIntent: RecoveryIntent | undefined =
    intent === 'backup' ? { kind: 'backup' } : intent === 'test_restore' && point ? { kind: 'test_restore', recoveryPointId: point } : undefined;

  function clearIntent() {
    if (!searchParams.has('intent')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('intent');
    next.delete('point');
    setSearchParams(next, { replace: true });
  }

  if (!environment) {
    return (
      <ProjectSection>
        <EmptyState
          title="No environment selected"
          description="Add an environment in Settings."
        />
      </ProjectSection>
    );
  }

  if (openRunId) {
    return (
      <ProjectSection>
        <ExecutionRunView
          executionId={openRunId}
          sectionPath={project.sectionPath}
          onSettled={project.refresh}
          onBack={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('run');
            setSearchParams(next, { replace: true });
            project.refresh();
          }}
        />
      </ProjectSection>
    );
  }

  const magento = project.applications.find((app) => app.type === 'magento_backend') ?? null;
  const placement = magento ? project.environmentOverview.data?.placements.find((p) => p.application_id === magento.id) : undefined;
  const importPanel = project.isWordPress || !magento ? null : (
    <ImportStorePanel
      key={`import-${environment.id}`}
      projectId={project.projectId}
      environment={environment}
      magentoApplicationId={magento.id}
      serverName={placement ? project.serverName(placement.server_id) : 'the server'}
      ready={project.placedPrimary}
      runPath={(executionId) => project.sectionPath('recovery', { run: executionId })}
      onImported={project.refresh}
      onDeploy={() => project.openDeploy([])}
    />
  );
  const nothingDeployed = (project.environmentOverview.data?.active_releases ?? []).length === 0;
  const points = recoveryPoints.data?.recovery_points ?? [];
  // An SSH environment keeps its backups on its own page. Say so and stop: an
  // empty list here would claim there are none (UX-08).
  const recordsElsewhere = isOperatedOverSSH(environment, project.isWordPress) && points.length === 0;
  // The newest backup that failed and has a run to read: where View error goes.
  const failedRun = [...points]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .find((rp) => (rp.status === 'failed' || rp.status === 'verification_failed') && rp.execution_id)?.execution_id;
  const backupNow = () => {
    const next = new URLSearchParams(searchParams);
    next.set('intent', 'backup');
    setSearchParams(next, { replace: true });
  };

  if (recordsElsewhere) {
    return (
      <ProjectSection>
        <SSHEnvironmentNotice environment={environment} what="Backups and restores" />
        {importPanel}
      </ProjectSection>
    );
  }

  return (
    <ProjectSection>
      {isOperatedOverSSH(environment, project.isWordPress) ? <SSHEnvironmentNotice environment={environment} what="Backups and restores" /> : null}
      <BackupPosition
        health={project.environmentHealth.data?.backups ?? null}
        environmentName={environment.name}
        loading={project.environmentHealth.loading}
        error={project.environmentHealth.error?.message}
        onViewError={failedRun ? () => navigate(project.sectionPath('recovery', { run: failedRun })) : undefined}
        onBackUp={backupNow}
      />
      {nothingDeployed ? importPanel : null}
      <RecoveryPointsView
        key={`recovery-${environment.id}`}
        projectId={project.projectId}
        environment={environment}
        projectName={project.project.name}
        recoveryPoints={recoveryPoints.data?.recovery_points ?? []}
        incompleteRestore={recoveryPoints.data?.incomplete_restore ?? null}
        loading={recoveryPoints.loading}
        onRefresh={project.refresh}
        onNavigateToExecution={(executionId) => navigate(project.sectionPath('recovery', { run: executionId }))}
        intent={recoveryIntent}
        onIntentHandled={clearIntent}
      />
      {nothingDeployed ? null : importPanel}
    </ProjectSection>
  );
}
